import React, { useEffect, useState, useRef } from "react"; // Add useRef import
import { Modal, Card, Button } from "react-bootstrap";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  InfoWindow,
} from "@react-google-maps/api";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import moment from "moment/moment";
import { LoadingSpinner } from "../../../../../_metronic/_partials/controls/LoadingSpinner";
import {
  fetchNearSite,
  fetchOrderSummaryListStripe,
} from "../../_redux/order/orderAction";
import { getONAContainerSite } from "../../_redux/order/orderCrud";
import { Swiper, SwiperSlide } from "swiper/react";
// import required modules
import { Pagination, Navigation } from "swiper";
import { orderSlice } from "../../_redux/order/orderSlice";

const { actions } = orderSlice;

const mapContainerStyle = {
  width: "100%",
  minHeight: "70vh",
  maxHeight: "70vh",
};

const center = {
  lat: 51.509865, // Default latitude (example: London)
  lng: -0.118092, // Default longitude (example: London)
};

export const NearSiteModal = ({
  show,
  setShow,
  dispatchReducer,
  state,
  cardDetail,
  setONASiteData,
}) => {
  const dispatch = useDispatch();
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE,
    id: "google-map-script",
  });

  const [availSite, setAvailSite] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null); // Add state for selected marker
  const { siteData, siteLoading, orderListLoading } = useSelector(
    (state) => ({
      siteData: state.order.siteData,
      siteLoading: state.order.siteLoading,
      orderListLoading: state.order.callApi,
    }),
    shallowEqual
  );

  const mapRef = useRef(null); // Create a reference for the map

  const handleMapLoad = (map) => {
    mapRef.current = map; // Store the map instance
  };

  const zoomToLocation = (lat, lng, zoomLevel = 11) => {
    if (mapRef.current) {
      mapRef.current.panTo({ lat, lng }); // Pan to the specified location
      mapRef.current.setZoom(zoomLevel); // Set the zoom level
    } else {
      console.error("Map reference is not available.");
    }
  };

  const handleRadioChange = (e, siteData) => {
    zoomToLocation(Number(e.latitude), Number(e.longitude));
    setONASiteData(siteData?.containers);
    dispatchReducer({
      type: "SET_DATA",
      data: {
        ...state,
        siteSelect: e,
        planReplaced: 0,
        franchise_id: e.franchise_id,
      },
    });
    dispatch(
      fetchOrderSummaryListStripe({
        franchise_id: e.franchise_id,
        postcode: state?.postcode,
        site_id: e?.id,
      })
    );
    dispatch(
      actions.newCustomerFetched({
        id: e.franchise_id,
        chargebee_key: e?.franchise?.chargebee_key,
        stripe_pk: e?.franchise?.stripe_pk,
      })
    );
    // Pan and zoom the map to the selected site's location
    if (mapRef.current && e.lat && e.lng) {
      mapRef.current.panTo({ lat: e.lat, lng: e.lng });
      mapRef.current.setZoom(20); // Set zoom level to focus on the site
    }
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
  };

  // const zoomToLocation = (lat, lng) => {
  //   if (mapRef.current) {
  //     mapRef.current.panTo({ lat, lng });
  //     mapRef.current.setZoom(14); // Adjust zoom level as needed
  //   }
  // };

  const geocodeAddress = async (address) => {
    const geocoder = new window.google.maps.Geocoder();
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
          });
        } else {
          console.error("Geocode failed: ", status);
          reject(status);
        }
      });
    });
  };

  useEffect(() => {
    if (
      cardDetail &&
      cardDetail.length &&
      cardDetail[0]?.contact?.franchise_id
    ) {
      const data = {
        franchise_id: cardDetail[0]?.contact?.franchise_id,
        containerSize: state?.containerListSelect,
        type: state?.containerList?.find(
          (e) => e.id === state?.containerListSelect
        )?.type,
      };
      const address = cardDetail[0]?.address + cardDetail[0]?.postcode;

      dispatch(fetchNearSite(data, address));
    }
  }, []);

  useEffect(() => {
    getONAContainerSite(state?.siteSelect?.id).then((res) => {
      setAvailSite(res?.data?.data);
    });
  }, []);

  useEffect(() => {
    const fetchMarkers = async () => {
      if (siteData && siteData.length > 0) {
        const updatedMarkers = await Promise.all(
          siteData.map(async (site) => {
            const address = site.address; // Replace with the actual address field
            try {
              const coordinates = await geocodeAddress(address);
              return {
                lat: coordinates.lat,
                lng: coordinates.lng,
                name: site.name,
              };
            } catch (error) {
              console.error("Error fetching coordinates for address:", address);
              return null;
            }
          })
        );

        setMarkers(updatedMarkers.filter((marker) => marker !== null));
      }
    };

    if (isLoaded) {
      fetchMarkers();
    }
  }, [siteData, isLoaded]);

  useEffect(() => {
    if (siteData && siteData.length > 0) {
      const firstSite = siteData[0];
      if (firstSite.latitude && firstSite.longitude) {
        zoomToLocation(
          parseFloat(firstSite.latitude),
          parseFloat(firstSite.longitude)
        );
      }
    }
  }, [siteData]);

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker); // Set the selected marker when a pin is clicked
  };

  const handleProceed = () => {
    if (selectedMarker) {
      const selectedSite = siteData.find(
        (site) => site.name === selectedMarker.name
      );
      if (selectedSite) {
        const availableSites = Array.isArray(availSite)
          ? availSite
          : Object.values(availSite || {}); // Ensure availSite is an array
        handleRadioChange(
          selectedSite,
          availableSites.find((item) => item.site_id === selectedSite.id)
        );
      }
    }
    setShow(false); // Close the modal
  };

  if (loadError) {
    console.error("Google Maps API failed to load:", loadError);
    return <div>Error loading Google Maps</div>;
  }

  if (!isLoaded) {
    return <LoadingSpinner />;
  }

  return (
    <Modal
      size="xl"
      show={show.open}
      onHide={() => setShow(false)}
      aria-labelledby="example-modal-sizes-title-xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title id="example-custom-modal-styling-title">
          Select Site{" "}
          <span className="text-warning">
            (Please click on the PIN on MAP and then the PROCEED button)
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          {/* Left Column: Detail Cards */}
          <div
            className="col-md-4"
            style={{
              maxHeight: "70vh",
              overflowY: "auto",
              paddingRight: "8px",
            }}
          >
            {(siteLoading || orderListLoading) && <LoadingSpinner />}
            {!orderListLoading && (
              <>
                {siteData && siteData.length > 0
                  ? siteData.map((e, index) => {
                      const siteAvail = Object.values(availSite)?.find(
                        (item) => item.site_id === e.id
                      );

                      return (
                        <div
                          key={e.id}
                          onClick={() =>
                            e.capacity && handleRadioChange(e, siteAvail)
                          }
                          style={{
                            padding: "12px 0",
                            borderBottom:
                              index !== siteData.length - 1
                                ? "1px solid #ccc"
                                : "none",
                            cursor: e.capacity ? "pointer" : "not-allowed",
                          }}
                        >
                          {/* Top Row: Checkbox + Name + Distance */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "6px",
                            }}
                          >
                            <label
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <input
                                type="radio" // Change from checkbox to radio
                                name="siteSelection" // Ensure all radio buttons share the same name
                                checked={state?.siteSelect?.id === e.id}
                                onClick={(ev) => ev.stopPropagation()}
                                onChange={() =>
                                  e.capacity && handleRadioChange(e)
                                }
                                // disabled={!e.capacity}
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  marginRight: "10px",
                                  cursor: "pointer",
                                  accentColor: "#007bff", // Blue color when checked
                                  transition: "0.3s ease", // Smooth transition on hover and focus
                                }}
                              />
                              <span
                                style={{
                                  fontWeight: "bold",
                                  color: "#222",
                                  marginLeft: "10px",
                                  fontSize: "16px",
                                }}
                              >
                                {e.name}
                              </span>
                            </label>
                            <span style={{ fontSize: "14px", color: "#555" }}>
                              {(e.distance * 0.621371).toFixed(2)} miles
                            </span>
                          </div>

                          {/* Franchise */}
                          <div
                            style={{ fontSize: "14px", marginBottom: "4px" }}
                          >
                            <strong>Franchise:</strong>{" "}
                            {e.franchise?.name || "-"}
                          </div>

                          {/* Capacity */}
                          <div
                            style={{ fontSize: "14px", marginBottom: "4px" }}
                          >
                            <strong>Available:</strong> {e.capacity || "-"}
                          </div>

                          {/* Available From */}
                          <div
                            style={{ fontSize: "14px", marginBottom: "4px" }}
                          >
                            <strong>Available From:</strong>{" "}
                            {e.available_from
                              ? moment(e.available_from).format("DD-MM-yyyy")
                              : "-"}
                          </div>

                          {/* Address */}
                          <div style={{ fontSize: "14px" }}>
                            <strong>Address:</strong> {e.address}
                          </div>

                          {/* Swiper for Images */}
                          {/* <div style={{ marginTop: "10px" }}>
                        <Swiper
                          pagination={true}
                          modules={[Pagination]}
                          className="newSwiper"
                          style={{ width: "100%", height: "180px" }}
                        >
                          {e.images &&
                            e.images.map((value, i) => (
                              <SwiperSlide key={i}>
                                <img
                                  src={value.image}
                                  alt="site-img"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                  }}
                                />
                              </SwiperSlide>
                            ))}
                        </Swiper>
                      </div> */}
                        </div>
                      );
                    })
                  : ""}
              </>
            )}
          </div>

          {/* Right Column: Google Map */}
          <div className="col-md-8">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={7}
              options={{
                disableDefaultUI: true, // Disable all default UI controls
                zoomControl: true, // Enable only zoom controls (plus and minus)
              }}
              onLoad={handleMapLoad} // Attach the map load handler
            >
              {siteData
                .filter((marker) => marker.latitude && marker.longitude) // Ensure valid coordinates
                .map((marker, index) => (
                  <Marker
                    key={index}
                    position={{
                      lat: parseFloat(marker.latitude), // Convert latitude to a number
                      lng: parseFloat(marker.longitude), // Convert longitude to a number
                    }}
                    title={marker.name}
                    onClick={() => handleMarkerClick(marker)} // Add click handler
                  />
                ))}
              {selectedMarker && (
                <InfoWindow
                  position={{
                    lat: parseFloat(selectedMarker.latitude), // Ensure lat is a number
                    lng: parseFloat(selectedMarker.longitude), // Ensure lng is a number
                  }}
                  onCloseClick={() => setSelectedMarker(null)} // Close the info window
                >
                  <div>
                    <h6>{selectedMarker.name}</h6>
                    <div style={{ textAlign: "center" }}>
                      <Button
                        variant="primary"
                        size="sm" // Make the button smaller
                        onClick={handleProceed} // Handle proceed button click
                        // disabled={!selectedMarker?.capacity}
                      >
                        Proceed
                      </Button>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>
          Close
        </Button>
        {/* <Button
          variant="primary"
          onClick={() => setShow(false)}
          disabled={!state?.siteSelect?.id}
        >
          Proceed
        </Button> */}
      </Modal.Footer>
    </Modal>
  );
};
